import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useHistory } from "react-router-dom";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderRow,
    DataTableOverflowMenuCell,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    List,
    ListItem,
    TextButton,
} from "@episerver/ui-framework";
import {
    confirmDialogSelector,
    initialAddressState,
    onChangeAddressPage,
    onChangeAddressRowPerPage,
    onChangeConfirmDialog,
    paginationSelector,
} from "../reducers/addressListReducer";
import { contactSelector, onDeleteContactAddress } from "../../ContactManager/reducers/contactDetailReducer";

import { Address, InView } from "../model/address";
import { initialAddressFormState, onOpenDialog } from "../reducers/addressFormReducer";
import {
    organizationSelector,
    onDeleteOrganizationAddress,
} from "../../OrganizationManager/reducers/organizationDetailReducer";
import {fetchAddressesInContact, fetchAddressesInOrganization} from "../reducers/asyncThunks";

interface AddressListProps {
    inTab?: boolean;
    inView?: InView;
    viewId: string;
}

const AddressList = (props: AddressListProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { inTab = false, inView = InView.None, viewId } = props;
    const contact =  useSelector(contactSelector);
    const organization =  useSelector(organizationSelector);
    const addresses = inView === InView.Contact ? contact.addresses : organization.addresses;   
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useSelector(paginationSelector);
    const confirmDialog = useSelector(confirmDialogSelector);

    useEffect(() => {
        if (inView === InView.Contact) dispatch(fetchAddressesInContact(viewId));
        else dispatch(fetchAddressesInOrganization(viewId));
    }, []);

    const columns: Array<{ selector: string; title: string }> = [
        { selector: "name", title: "Name" },
        { selector: "line1", title: "Line #1" },
        { selector: "city", title: "City" },
        { selector: "postal Code", title: "PostalCode" },
        { selector: "country Code", title: "CountryCode" },
        { selector: "", title: "" },
    ];

    const onConfirmDialog = (address: Address, message: string): void => {
        dispatch(onChangeConfirmDialog({ ...confirmDialog, open: true, address: address, message: message }));
    };

    const onDelete = async (): Promise<void> => {
        if (inView === InView.Contact) {
            await dispatch(onDeleteContactAddress(confirmDialog.address));
        } else if (inView === InView.Organization) {
            await dispatch(onDeleteOrganizationAddress(confirmDialog.address));
        }
        dispatch(onChangeConfirmDialog(initialAddressState.confirmDialog));
    };

    const onCancel = (): void => {
        dispatch(onChangeConfirmDialog(initialAddressState.confirmDialog));
    };

    const renderRow = (rowData: Address, idx: number): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId}>
                <DataTableCell>{rowData.name ?? ""}</DataTableCell>
                <DataTableCell>{rowData.line1 ?? ""}</DataTableCell>
                <DataTableCell>{rowData.city ?? ""}</DataTableCell>
                <DataTableCell>{rowData.postalCode ?? ""}</DataTableCell>
                <DataTableCell>{rowData.countryCode ?? ""}</DataTableCell>
                <DataTableOverflowMenuCell>
                    <List>
                        <ListItem
                            onClick={() =>
                                dispatch(
                                    onOpenDialog({
                                        form: {
                                            open: true,
                                            title: "Edit an address",
                                            address: {
                                                ...rowData,
                                                isNew: false,
                                                idx: idx
                                            },
                                            extendedPropertyErrors: [],
                                        },
                                    })
                                )
                            }
                        >
                            Edit
                        </ListItem>
                        <ListItem onClick={() => onConfirmDialog(rowData, `Are you sure to delete "${rowData.name}"?`)}>
                            Delete
                        </ListItem>
                    </List>
                </DataTableOverflowMenuCell>
            </DataTableRow>
        );
    };

    return (
        <div className="customer-list">
            {!inTab && (
                <Fragment>
                    <h1 className="page-header">Manage Addresses</h1>
                    <br />
                </Fragment>
            )}
            <DataTable>
                <DataTableToolbar>
                    <TextButton
                        onClick={() =>
                            dispatch(
                                onOpenDialog({
                                    form: {
                                        open: true,
                                        title: "Add an address",
                                        address: {
                                            ...initialAddressFormState.form.address,
                                            [inView === InView.Organization ? "organizationId" : "contactId"]: viewId,
                                            primaryKeyId: "00000000-0000-0000-0000-000000000000",
                                            isNew: true,
                                            idx: addresses.length
                                        },
                                        extendedPropertyErrors: [],
                                    },
                                })
                            )
                        }
                    >
                        Create
                    </TextButton>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <React.Fragment>
                            {columns.map((column, index) => {
                                return (
                                    <DataTableColumnHeaderCell key={index}>{column.title}</DataTableColumnHeaderCell>
                                );
                            })}
                        </React.Fragment>
                    </DataTableHeaderRow>
                    <DataTableBody>
                        {addresses.map((row: Address, index: number) => {
                            if (
                                index < startRow ||
                                index >= startRow + rowsPerPage ||
                                row.primaryKeyId == "00000000-0000-0000-0000-000000000000"
                            ) {
                                return <React.Fragment key={row.primaryKeyId}></React.Fragment>;
                            }
                            return renderRow(row, index);
                        })}
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={(startRow) => dispatch(onChangeAddressPage(startRow))}
                    onChangeRowsPerPage={(rowsPerPage) => dispatch(onChangeAddressRowPerPage(rowsPerPage))}
                />
            </DataTable>
            {!inTab && (
                <Fragment>
                    <br />
                    <br />
                    <div className="edit-form-buttons">
                        <TextButton onClick={() => history.goBack()}>Back</TextButton>
                    </div>
                </Fragment>
            )}

            {/* Delete dialog */}
            <Dialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                dismissLabel="Cancel"
                confirmLabel="Delete"
                enableConfirm
                onInteraction={(e) => {
                    if (e.detail.action === "confirm") onDelete();
                    else onCancel();
                }}
            >
                <React.Fragment>
                    <p>{confirmDialog.message}</p>
                </React.Fragment>
            </Dialog>
        </div>
    );
};

export default AddressList;
